import api from "./api";
import axios from "axios";
import { updateFileDO, uploadFileDO } from "./digitaloceanServices";
import { getBankIdByCode } from "./documentServices";
import {
  AttachmentTypeIdEnum,
  DocumentInterface,
  EntityIdEnum,
  PaymentDetailsDto,
  PaymentInterface,
  PaymentMethodEnum,
  ResponseInterface,
  UserInterface,
} from "../interfaces";

const baseURLAPI = `${process.env.REACT_APP_API_HOST}/V1/Payment`;

// Method to save proof of payment
export const saveProofOfPayment = async (
  file: File,
  retention: PaymentInterface,
  user: UserInterface,
  statusID: number,
  reference?: string,
  isModification?: boolean,
  documentID?: string
): Promise<ResponseInterface<string>> => {
  let response: ResponseInterface<string> = {
    message: "",
    model: null,
    didError: true,
    errorMessage: "",
  };
  if (!retention.paymentDetailID) {
    response.errorMessage = "No se encontró el PaymentDetailID";
    return response;
  }

  try {
    if (isModification) {
      const resUpdateDO = await updateFileDO(
        file,
        retention.proofOfPayment!,
        user?.userLogin
      );

      if (!resUpdateDO.didError) {
        const resUpdate = await updatePayment({
          documentID: documentID ?? retention.documentID!,
          paymentDetailID: retention.paymentDetailID,
          referenceNum: reference ?? undefined,
          statusID: statusID,
          user: user,
        });
        if (!resUpdate.didError) {
          response.model = retention.proofOfPayment!;
          response.didError = false;
          return response;
        }

        response.didError = true;
        response.errorMessage = resUpdate.errorMessage;
        return response;
      }
      response.errorMessage =
        "Hubo un error al actualizar el documento adjunto en DigitalOcean";
      return response;
    } else {
      const resUpload = await uploadFileDO(
        file,
        EntityIdEnum.PAYMENT,
        AttachmentTypeIdEnum.PAYMENT_PROOF,
        user.userLogin
      );
      if (!resUpload.didError) {
        const resUpdate = await updatePayment({
          documentID: documentID ?? retention.documentID!,
          paymentDetailID: retention.paymentDetailID,
          attachmentID: resUpload.model?.attachmentID,
          statusID: statusID,
          referenceNum: reference ?? undefined,
          user: user,
        });
        if (!resUpdate.didError) {
          response.model = resUpload.model?.attachmentID!;
          response.didError = false;
          return response;
        }
        response.errorMessage = resUpdate.errorMessage;
        return response;
      }

      response.didError = true;
      response.errorMessage =
        "Hubo un error al guardar el documento adjunto en DigitalOcean";
      return response;
    }
  } catch (error) {
    return (error as any)?.response?.data;
  }
};

// Method to save payment
export const savePayment = async (
  payment: PaymentInterface,
  document: DocumentInterface,
  buCode?: string,
  user?: UserInterface,
  currencyID: number = 2
): Promise<ResponseInterface<{ paymentID: string }>> => {
  const bankID = getBankIdByCode(payment.bank ?? "0000");
  // Get date with UTC-4
  const date = new Date();
  date.setHours(date.getHours() - 4);

  const body = JSON.stringify({
    DocumentId: document.documentID,
    PaymentAmount: +(payment.paymentAmount?.toFixed(2) ?? 0),
    PaymentDate: date.toISOString(),
    BUCodePayment: buCode,
    StatusID: payment.status,
    CreationUser: user?.userLogin,
    PaymentDetails: [
      {
        CurrencyID: currencyID,
        BankAccountID:
          payment.paymentMethod.bankAccountID ??
          payment.paymentMethod.bankAccounts?.find(
            (account) => account.bankID === payment.destBankID
          )?.bankAccountID,
        SourceBankID: [
          PaymentMethodEnum.PAGO_MOVIL_B2P_VUELTO,
          PaymentMethodEnum.REINTEGRO,
          PaymentMethodEnum.CREDITO_INMEDIATO,
          PaymentMethodEnum.CREDITO_INMEDIATO_CM,
          PaymentMethodEnum.CREDITO_INMEDIATO_CUENTA,
        ].includes(payment.paymentMethod.paymentMethodID)
          ? payment.destBankID
          : bankID,
        PaymentMethodID: payment.paymentMethod.paymentMethodID,
        Amount: +(payment.paymentAmount?.toFixed(2) ?? 0),
        PaymentDate: date.toISOString(),
        ReferenceNum: payment.reference,
        PctIGTFTax: payment.igtfAmount,
        IGTFTaxPercentage: payment.igtfPercentage,
      },
    ],
    PaymentElectronicDetails: {
      IdentificationNumber: payment.clientIdentifier ?? "",
      BankID: [
        PaymentMethodEnum.PAGO_MOVIL_B2P_VUELTO,
        PaymentMethodEnum.REINTEGRO,
        PaymentMethodEnum.CREDITO_INMEDIATO,
        PaymentMethodEnum.CREDITO_INMEDIATO_CM,
        PaymentMethodEnum.CREDITO_INMEDIATO_CUENTA,
      ].includes(payment.paymentMethod.paymentMethodID)
        ? bankID
        : payment.destBankID ?? 0,
      PhoneNumber: payment.phone ?? "",
    },
  });

  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const uri = `${baseURLAPI}/Insert`;

  try {
    const response = await api.post(uri, body, {
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken: source.token,
    });

    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return (error as any)?.response?.data;
  }

  return {
    message: "",
    model: null,
    didError: true,
    errorMessage: "Hubo un error al guardar el pago.",
  };
};

interface UpdatePaymentOptions {
  documentID: string;
  paymentDetailID: string;
  bankAccountID?: number;
  sourceBankID?: number;
  attachmentID?: string;
  referenceNum?: string;
  statusID?: number;
  user: UserInterface;
}

// Method to update payment
export const updatePayment = async ({
  documentID,
  paymentDetailID,
  bankAccountID,
  sourceBankID,
  attachmentID,
  referenceNum,
  statusID,
  user,
}: UpdatePaymentOptions): Promise<ResponseInterface<boolean>> => {
  const body = JSON.stringify({
    DocumentID: documentID,
    PaymentDetailID: paymentDetailID,
    BankAccountID: bankAccountID,
    SourceBankID: sourceBankID,
    AttachmentID: attachmentID,
    ReferenceNum: referenceNum,
    StatusID: statusID,
    UpdateUser: user?.userLogin,
    UpdateDate: new Date().toISOString(),
  });

  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const uri = `${baseURLAPI}/Update`;

  try {
    const response = await api.put(uri, body, {
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken: source.token,
    });

    return response.data;
  } catch (error) {
    return (error as any)?.response?.data;
  }
};

// Method to get payments with extended details
export const getPaymentsDetails = async ({
  documentID,
  paymentID,
  paymentDate,
}: {
  documentID?: string;
  paymentID?: string;
  paymentDate?: Date;
}): Promise<ResponseInterface<PaymentDetailsDto[]>> => {
  const body = {
    DocumentID: documentID,
    PaymentID: paymentID,
    PaymentDate: paymentDate ? paymentDate.toISOString() : undefined,
  };
  const uri = `${baseURLAPI}/GetAllV2`;
  try {
    const response = await api.post(uri, body, {
      headers: { "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error) {
    console.error("Error getting Payment Details:", error);
    return (error as any)?.response?.data;
  }
};
